import { isEmptyWhereClause } from '@modules/common/utils';
import { getCombinedWhereClause } from '@modules/dashboard/helpers';
import {
  CompareQueryProperty,
  QuantifyAndDashboardFilters,
  QuantifyDashboardFilters,
  QuantifyQuery,
} from '@modules/quantify/store/types';
import { getCompareQueryPreviousPeriodFromDuration } from '@modules/quantify/utils/getCompareToPreviousPeriodFromDuration';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

interface GetCombinedQuantifyAndDashboardFiltersOptions {
  primaryQuery: Omit<
    QuantifyQuery,
    'groupBy' | 'shouldShowNormalisedValues' | 'measuredAsCriteria'
  >;
  dashboardFilters: QuantifyDashboardFilters | null;
}

export const getCombinedQuantifyAndDashboardFilters = ({
  primaryQuery,
  dashboardFilters,
}: GetCombinedQuantifyAndDashboardFiltersOptions): QuantifyAndDashboardFilters => {
  return {
    whereClause: getCombinedWhereClauseFilter(
      primaryQuery.whereClause ?? EmptyWhereClause,
      dashboardFilters?.whereClause ?? EmptyWhereClause,
    ),
    denominatorWhereClause: getCombinedDenominatorWhereClauseFilter(
      primaryQuery.formulaQuery?.denominatorWhereClause ?? EmptyWhereClause,
      dashboardFilters?.whereClause ?? EmptyWhereClause,
      primaryQuery.formulaQuery?.eqlFunction ?? EQLFunctionName.Count,
    ),
    compareQuery: getCombinedCompareQueryQuery(
      primaryQuery.compareQuery,
      dashboardFilters,
      primaryQuery.formulaQuery?.eqlFunction ?? EQLFunctionName.Count,
    ),
  };
};

const getCombinedWhereClauseFilter = (
  primaryWhereClause: WhereClause,
  dashboardWhereClause: WhereClause,
): WhereClause => {
  const isEmptyDashboardWhereClause = isEmptyWhereClause(dashboardWhereClause);

  if (isEmptyDashboardWhereClause) {
    return primaryWhereClause;
  }

  return getCombinedWhereClause(primaryWhereClause, dashboardWhereClause);
};

const getCombinedDenominatorWhereClauseFilter = (
  primaryDenominatorWhereClause: WhereClause,
  dashboardWhereClause: WhereClause,
  formulaID: string,
): WhereClause => {
  if (formulaID !== EQLFunctionName.NormaliseByPercent) {
    return primaryDenominatorWhereClause;
  }

  const isEmptyDashboardWhereClause = isEmptyWhereClause(dashboardWhereClause);

  if (isEmptyDashboardWhereClause) {
    return primaryDenominatorWhereClause;
  }

  return getCombinedWhereClause(
    primaryDenominatorWhereClause,
    dashboardWhereClause,
  );
};

const getCombinedCompareQueryQuery = (
  primaryCompareQuery: QuantifyQuery['compareQuery'],
  dashboardFilters: QuantifyDashboardFilters | null,
  formulaID: string,
): CompareQueryProperty | undefined => {
  const {
    duration: dashboardDuration,
    whereClause: dashboardWhereClause = EmptyWhereClause,
  } = dashboardFilters ?? {};

  if (!primaryCompareQuery) {
    return undefined;
  }

  if (!!primaryCompareQuery.previousPeriod) {
    return dashboardDuration?.type === 'last'
      ? getCompareQueryPreviousPeriodFromDuration(dashboardDuration)
      : primaryCompareQuery;
  }

  if (!!primaryCompareQuery.timerange) {
    return primaryCompareQuery; // TODO: Make changes to support other types of compareQuery duration after discussion
  }

  if (isEmptyWhereClause(dashboardWhereClause)) {
    return primaryCompareQuery;
  }

  const updatedDenominatorWhereClause =
    formulaID === EQLFunctionName.NormaliseByPercent
      ? getCombinedWhereClause(
          primaryCompareQuery.formula?.denominatorWhereClause ??
            EmptyWhereClause,
          dashboardWhereClause,
        )
      : primaryCompareQuery.formula?.denominatorWhereClause ?? EmptyWhereClause;

  return {
    ...primaryCompareQuery,
    whereClause: getCombinedWhereClause(
      primaryCompareQuery.whereClause ?? EmptyWhereClause,
      dashboardWhereClause,
    ),
    formula: {
      ...primaryCompareQuery.formula,
      denominatorWhereClause: updatedDenominatorWhereClause,
    },
  };
};
